import { useState } from 'react';
import {
  makeStyles, tokens, Spinner, Badge,
  Dialog, DialogSurface, DialogTitle, DialogBody, DialogContent, DialogActions, Button,
} from '@fluentui/react-components';
import { useQuery } from '@tanstack/react-query';
import { useT } from '../i18n';
import { useIntlFormat } from '../hooks/useIntlFormat';
import { useLocaleSettings } from '../hooks/useLocaleSettings';
import { useFsmaEnabled } from '../hooks/useFsmaEnabled';
import { KdeStatusBadge } from '../components/traceability/KdeStatusBadge';
import { KpiCard } from '../components/shared/KpiCard';
import {
  fetchExportShipments, fetchExportKpis, fetchExportShipment,
  type ExportShipment, type ShipmentExportStatus,
} from '../api/export.api';
import { PageTitle } from '../components/shared/PageTitle';
import { DataTable } from '../components/shared/DataTable';
import { FilterPanel } from '../components/shared/FilterPanel';

const useStyles = makeStyles({
  root:    { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalL, padding: tokens.spacingVerticalL },
  kpiRow:  { display: 'flex', gap: tokens.spacingHorizontalM, flexWrap: 'wrap' },
  toolbar: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  table:   { width: '100%', overflowX: 'auto' as const },
  th:      { fontWeight: tokens.fontWeightSemibold, padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`, textAlign: 'left' as const },
  td:      { padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`, textAlign: 'left' as const },
  events:  { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXS },
  event:   { display: 'flex', gap: tokens.spacingHorizontalS, fontSize: tokens.fontSizeBase200 },
});

const STATUS_COLOR: Record<ShipmentExportStatus, 'success' | 'warning' | 'danger' | 'subtle'> = {
  DRAFT:              'subtle',
  PACKING:            'subtle',
  SEALED:             'warning',
  DEPARTED:           'warning',
  CUSTOMS_SUBMITTED:  'warning',
  CUSTOMS_CLEARED:    'success',
  CUSTOMS_HOLD:       'danger',
  FDA_SUBMITTED:      'warning',
  FDA_CLEARED:        'success',
  FDA_HOLD:           'danger',
  ARRIVED:            'success',
  DELIVERED:          'success',
  CANCELLED:          'subtle',
};

/** KDE de envío (21 CFR 1.1340) que faltan en un embarque; no inventa valores. */
export function shipmentKdeMissing(s: ExportShipment): string[] {
  const m: string[] = [];
  if (!s.tlcs?.length) m.push('tlc');
  if (!s.recipientLocation) m.push('recipientLocation');
  if (!s.originLocation) m.push('originLocation');
  if (!(s.shipDate || s.actualDeparture || s.departedAt)) m.push('shipDate');
  if (!s.referenceDocType || !s.referenceDocNumber) m.push('referenceDocNumber');
  return m;
}

export function ExportPage() {
  const t = useT();
  const { fmtNum, fmtDateTime } = useIntlFormat();
  const s = useStyles();
  const { formatDate } = useLocaleSettings();

  const [statusFilter, setStatusFilter] = useState<ShipmentExportStatus[]>([]);
  const [selected, setSelected] = useState<string | null>(null);

  const { data: kpis, isLoading: kpisLoading } = useQuery({
    queryKey: ['export-kpis'],
    queryFn:  () => fetchExportKpis(),
  });

  const { data: listData, isLoading: listLoading } = useQuery({
    queryKey: ['export-shipments'],
    queryFn:  () => fetchExportShipments(),
  });

  const { data: detail, isLoading: detailLoading } = useQuery({
    queryKey: ['export-detail', selected],
    queryFn:  () => fetchExportShipment(selected!),
    enabled:  !!selected,
  });

  const fsmaEnabled = useFsmaEnabled();
  const allShipments = listData?.shipments ?? [];
  const shipments = statusFilter.length ? allShipments.filter(sh => statusFilter.includes(sh.status)) : allShipments;
  const shipDate = detail ? (detail.shipment.shipDate || detail.shipment.actualDeparture || detail.shipment.departedAt) : undefined;

  function statusLabel(st: ShipmentExportStatus): string {
    const key = `status${st}` as keyof typeof t.export;
    return String(t.export[key] ?? st);
  }

  return (
    <div className={s.root}>
      <PageTitle>{t.export.title}</PageTitle>

      {/* KPIs */}
      {kpisLoading && <Spinner size="small" label={t.export.loading} />}
      {kpis && (
        <div className={s.kpiRow}>
          <KpiCard label={t.export.kpiTotal}      value={String(kpis.totalShipments)} />
          <KpiCard label={t.export.kpiInTransit}  value={String(kpis.activeShipments)} />
          <KpiCard label={t.export.kpiHeld}        value={String(kpis.onHoldShipments)} accent={kpis.onHoldShipments > 0} />
          <KpiCard label={t.export.kpiDelivered}  value={String(kpis.deliveredShipments)} />
          <KpiCard label={t.export.kpiKg}          value={fmtNum(kpis.exportKgTotal)} />
          {kpis.avgTransitDays !== undefined && (
            <KpiCard label={t.export.kpiTransitDays} value={`${kpis.avgTransitDays.toFixed(1)} ${t.export.daysUnit}`} />
          )}
        </div>
      )}

      {/* Filtro estado */}
      <FilterPanel
        onClearAll={() => setStatusFilter([])}
        dimensions={[{
          key: 'status',
          label: t.common.status,
          options: (Object.keys(STATUS_COLOR) as ShipmentExportStatus[]).map(st => ({ value: st, label: statusLabel(st) })),
          selected: statusFilter,
          onApply: (v) => setStatusFilter(v as ShipmentExportStatus[]),
          onClear: () => setStatusFilter([]),
        }]}
      />

      {/* Tabla */}
      {listLoading && <Spinner size="small" label={t.export.loading} />}
      <DataTable<ExportShipment>
        rows={listLoading ? [] : shipments}
        rowKey={(sh) => sh.shipmentId}
        emptyMessage={listLoading ? '…' : t.export.noData}
        onRowClick={(sh) => setSelected(sh.shipmentId)}
        columns={[
          { key: 'containerId', header: t.export.tableContainer, render: (sh) => sh.containerId ?? '—' },
          { key: 'product', header: t.export.tableProduct, render: (sh) => `${sh.product} ${sh.variety ? `(${sh.variety})` : ''}`.trim() },
          { key: 'destinationCountry', header: t.export.tableDestination, render: (sh) => sh.destinationCountry ?? '—' },
          { key: 'kg', header: t.export.tableKg, render: (sh) => fmtNum(sh.actualKg ?? sh.estimatedKg ?? 0) },
          { key: 'boxCount', header: t.export.tableBoxes, render: (sh) => sh.boxCount ?? '—' },
          { key: 'status', header: t.export.tableStatus, render: (sh) => <Badge appearance="tint" color={STATUS_COLOR[sh.status]}>{statusLabel(sh.status)}</Badge> },
          { key: 'departed', header: t.export.tableDeparted, render: (sh) => (sh.actualDeparture ? formatDate(sh.actualDeparture) : '—') },
        ]}
      />

      {/* Dialog detalle */}
      <Dialog open={!!selected} onOpenChange={(_, d) => { if (!d.open) setSelected(null); }}>
        <DialogSurface style={{ maxWidth: 640 }}>
          <DialogTitle>{t.export.detailTitle}: {detail?.shipment.containerId ?? '—'}</DialogTitle>
          <DialogBody>
            <DialogContent>
              {detailLoading && <Spinner size="small" />}
              {detail && (
                <>
                  <p><strong>{t.export.tableProduct}:</strong> {detail.shipment.product} {detail.shipment.variety ?? ''}</p>
                  <p><strong>{t.export.tableDestination}:</strong> {detail.shipment.destinationCountry ?? '—'}</p>
                  <p><strong>{t.export.tableKg}:</strong> {fmtNum((detail.shipment.actualKg ?? detail.shipment.estimatedKg ?? 0))}</p>
                  <p><strong>{t.export.tableStatus}:</strong> {statusLabel(detail.shipment.status)}</p>
                  {detail.shipment.buyer && <p><strong>Buyer:</strong> {detail.shipment.buyer}</p>}
                  {detail.shipment.carrier && <p><strong>Carrier:</strong> {detail.shipment.carrier}</p>}

                  {fsmaEnabled && (
                    <>
                      <h4>{t.export.kdeTitle}</h4>
                      <p><strong>{t.export.kdeRecipientLocation}:</strong> {detail.shipment.recipientLocation ?? '—'}</p>
                      <p><strong>{t.export.kdeOriginLocation}:</strong> {detail.shipment.originLocation ?? '—'}</p>
                      <p><strong>{t.export.kdeShipDate}:</strong> {shipDate ? formatDate(shipDate) : '—'}</p>
                      <p><strong>{t.export.kdeRefDoc}:</strong> {[detail.shipment.referenceDocType, detail.shipment.referenceDocNumber].filter(Boolean).join(' ') || '—'}</p>
                      <p><strong>{t.export.kdeTlcs}:</strong> {detail.shipment.tlcs?.join(', ') || '—'}</p>
                      <KdeStatusBadge missing={shipmentKdeMissing(detail.shipment)} />
                    </>
                  )}

                  <h4>{t.export.detailEventsTitle}</h4>
                  <div className={s.events}>
                    {detail.events.length === 0 && <span>—</span>}
                    {detail.events.map(ev => (
                      <div key={ev.eventId} className={s.event}>
                        <span>{fmtDateTime(ev.occurredAt)}</span>
                        <Badge appearance="tint" size="small">{ev.eventType}</Badge>
                        {ev.location && <span>{ev.location}</span>}
                        {ev.temperatureC !== undefined && <span>{ev.temperatureC} °C</span>}
                      </div>
                    ))}
                  </div>
                </>
              )}
            </DialogContent>
          </DialogBody>
          <DialogActions>
            <Button onClick={() => setSelected(null)}>{t.common.cancel}</Button>
          </DialogActions>
        </DialogSurface>
      </Dialog>
    </div>
  );
}
